import { useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import {
  CardView,
  Card,
  Content,
  Heading,
  Text,
  Link,
  ProgressBar,
  ProgressCircle,
  ActionButtonGroup,
  ActionButton,
  Accordion,
  Disclosure,
  DisclosureTitle,
  DisclosurePanel,
  DisclosureHeader,
  DialogContainer,
  Dialog,
  AlertDialog,
  Form,
  TextField,
  ButtonGroup,
  Button,
  TooltipTrigger,
  Tooltip,
} from "@react-spectrum/s2";
import { useDragAndDrop } from "react-aria-components";

import ImageIllustration from "@react-spectrum/s2/illustrations/linear/Image";
import NoImageIllustration from "@react-spectrum/s2/illustrations/linear/NoImage";
import AddCircleIcon from "@react-spectrum/s2/icons/AddCircle";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import ImagesIcon from "@react-spectrum/s2/icons/Images";
import LinkOutIcon from "icon:@app/assets/S2_LinkOutSize100.svg";

import { S3Image } from "@app/components/S3Image";
import { AssetCard } from "./AssetCard";

import { useVideo, useUpdateVideo, useVideoTemplate } from "@app/hooks/videos";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };
import { alignCardView } from "./style" with { type: "macro" };

const illustratedStateStyle = style({
  display: "grid",
  gridTemplateAreas: ["icon heading", "icon content"],
  gridTemplateColumns: ["auto", "1fr"],
  gridTemplateRows: ["auto", "auto"],
  columnGap: 12,
  rowGap: 2,
  alignItems: "center",
  justifyContent: "center",
  width: 280,
  marginX: "auto",
});

const fullTextStyle = style({
  maxWidth: "100%",
  width: "full",
  truncate: true,
  font: "ui-sm",
});

const shrinkTextStyle = style({
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: "0%",
  maxWidth: "100%",
  truncate: true,
});

export const ScrapedImages = ({ videoId, queries = [], onMenuAction }) => {
  const params = useParams();

  const projectId = videoId || params?.id;

  const video = useVideo(projectId);
  const updateVideo = useUpdateVideo(projectId);
  const { urls = [] } = video.data || {};
  const template = useVideoTemplate(video.data?.templateId);
  const templateData = template.data;

  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [editAction, setEditAction] = useState(null);

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys, items) => {
      return items.map((item) => ({
        "image/scrapped": JSON.stringify(item),
      }));
    },
    renderDragPreview: (items) => {
      const [item] = items.map((it) => JSON.parse(it["image/scrapped"]));
      if (!item) {
        return null;
      }

      return (
        <div
          className={style({
            width: 120,
            aspectRatio: "16 / 10",
            borderRadius: "lg",
            overflow: "hidden",
            boxShadow: "dragged",
          })}
        >
          <S3Image
            src={item?.thumb ?? item.src}
            alt={item.alt || item.id}
            styles={style({
              size: "full",
              objectFit: "contain",
            })}
          />
        </div>
      );
    },
  });

  const filteredQueries = useMemo(
    () =>
      [...queries]
        // .filter((q) => q.isSuccess)
        .map((q) => ({
          ...q,
          data: q.data?.filter((asset) => asset.type === "image") || [],
        })),
    [queries],
  );

  const handleEditUrl = useCallback(
    (url, title, index) => {
      const newUrls = [...urls];
      newUrls[index] = [url, title];
      updateVideo.mutate({ urls: newUrls });
      setExpandedKeys((oldKeys) => new Set(oldKeys).add(url));
    },
    [urls, updateVideo.mutate],
  );

  const handleAddUrl = useCallback(
    (url, title) => {
      const newUrls = [[url, title], ...urls.filter(([u]) => u !== url)];
      updateVideo.mutate(
        { urls: newUrls },
        {
          onSuccess: () => {
            // Expand the newly added URL
            setExpandedKeys((oldKeys) => new Set(oldKeys).add(url));
          },
        },
      );
    },
    [urls, updateVideo.mutate],
  );

  const handleDeleteUrl = useCallback(
    (url) => {
      const newUrls = urls.filter(([u]) => u !== url);
      updateVideo.mutate({ urls: newUrls });
      setExpandedKeys((oldKeys) => {
        const newSet = new Set(oldKeys);
        newSet.delete(url);
        return newSet;
      });
    },
    [urls, updateVideo.mutate],
  );

  const allSources = urls;

  useEffect(() => {
    if (video.isSuccess) {
      const incomingUrls = new Set([
        `${queries[0]?.query ?? queries[0]?.url ?? ""}-0`,
      ]);

      if (
        incomingUrls.size !== expandedKeys.size ||
        [...incomingUrls].some((url) => !expandedKeys.has(url))
      ) {
        setExpandedKeys(incomingUrls);
      }
    }
  }, [video.isSuccess, urls]);

  const renderEmptyState = () => (
    <div className={illustratedStateStyle}>
      <NoImageIllustration styles={style({ size: 48, gridArea: "icon" })} />
      <Text
        styles={style({
          font: "title-sm",
          gridArea: "heading",
          alignSelf: "end",
        })}
      >
        No images found
      </Text>
      <Text
        styles={style({
          font: "ui-sm",
          gridArea: "content",
          alignSelf: "start",
        })}
      >
        {"We couldn't find any images for this URL"}
      </Text>
    </div>
  );

  const renderLoadingState = () => (
    <div className={illustratedStateStyle}>
      <ImageIllustration styles={style({ size: 48, gridArea: "icon" })} />
      <Text
        styles={style({
          font: "title-sm",
          gridArea: "heading",
          alignSelf: "end",
        })}
      >
        Importing images
      </Text>
      <ProgressBar
        aria-label="Importing images..."
        size="S"
        isIndeterminate
        staticColor="auto"
        styles={style({
          gridArea: "content",
          alignSelf: "start",
          marginTop: 4,
          marginBottom: 8,
        })}
      />
    </div>
  );

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        marginTop: 4,
      })}
    >
      {createPortal(
        <TooltipTrigger>
          <ActionButton
            aria-label="Add new URL"
            isQuiet
            size="S"
            onPress={() => setEditAction({ type: "add", url: "", title: "" })}
            styles={style({ marginStart: "auto" })}
          >
            <AddCircleIcon />
            <Text>Add link</Text>
          </ActionButton>
          <Tooltip>Add new URL for image extraction</Tooltip>
        </TooltipTrigger>,
        document.getElementById("assets-menu"),
      )}

      <Accordion
        // allowsMultipleExpanded
        expandedKeys={expandedKeys}
        onExpandedChange={setExpandedKeys}
        density="spacious"
      >
        {filteredQueries.map((query, idx) => {
          const id = `${query?.query ?? query?.url ?? ""}-${idx}`;
          return (
            <Disclosure
              id={id}
              key={id}
              size={query?.label?.startsWith("[Search]") ? "S" : "M"}
            >
              <DisclosureHeader>
                <DisclosureTitle>
                  <div
                    className={style({
                      display: "inline-flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 4,
                      alignItems: "start",
                      overflow: "hidden",
                    })}
                  >
                    {!!query?.label && (
                      <Text styles={shrinkTextStyle}>{query?.label}</Text>
                    )}
                    <Text
                      styles={!!query?.label ? fullTextStyle : shrinkTextStyle}
                    >
                      <Link
                        href={query?.url}
                        target="_blank"
                        variant="secondary"
                      >
                        {query?.url}
                      </Link>
                    </Text>
                  </div>
                </DisclosureTitle>
                <ActionButtonGroup
                  size="S"
                  isQuiet
                  styles={style({ flexShrink: 0 })}
                >
                  {!!query.data && (
                    <ActionButton isDisabled>
                      <ImagesIcon />
                      <Text styles={style({ textOverflow: "unset" })}>
                        {query.data?.length}
                      </Text>
                    </ActionButton>
                  )}
                  <ActionButton
                    aria-label="Reload"
                    onPress={() => query.refetch()}
                    isDisabled={query.isFetching}
                  >
                    {query.isFetching ? (
                      <ProgressCircle
                        slot="icon"
                        aria-label={`Extracting images from ${query?.url}`}
                        isIndeterminate
                        size="S"
                        staticColor="auto"
                      />
                    ) : (
                      <RefreshIcon />
                    )}
                  </ActionButton>
                  {!!query.isEditable && (
                    <>
                      <ActionButton
                        aria-label="Delete link"
                        onPress={() =>
                          setEditAction({
                            type: "delete",
                            url: query?.url,
                          })
                        }
                      >
                        <DeleteIcon />
                      </ActionButton>
                      <ActionButton
                        aria-label="Edit link"
                        onPress={() =>
                          setEditAction({
                            type: "edit",
                            url: query?.url,
                            title: query?.label,
                            index: idx,
                          })
                        }
                      >
                        <EditIcon />
                      </ActionButton>
                    </>
                  )}
                </ActionButtonGroup>
              </DisclosureHeader>
              <DisclosurePanel>
                <CardView
                  aria-label={`Scraped images from ${query?.url}`}
                  textValue={`Scraped images from ${query?.url}`}
                  items={query.data || []}
                  dependencies={
                    process.env.NODE_ENV === "development" ? [+new Date()] : []
                  }
                  disabledKeys={
                    new Set(
                      query.data
                        ?.filter?.((item) => item.isDisabled)
                        .map((item) => item.id) ?? [],
                    )
                  }
                  dragAndDropHooks={dragAndDropHooks}
                  layout="grid"
                  variant="quiet"
                  size="S"
                  styles={style({ maxHeight: 480, ...alignCardView() })}
                >
                  {(item) => (
                    <Card id={item.id} textValue={item.alt || item.id}>
                      <AssetCard
                        item={item}
                        templateData={templateData}
                        onMenuAction={onMenuAction}
                      >
                        {!!item?.sourceUrl && (
                          <Link
                            href={item.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            staticColor="auto"
                            isStandalone
                            isQuiet
                            styles={style({
                              position: "absolute",
                              bottom: 8,
                              insetEnd: 12,
                            })}
                          >
                            <Text styles={style({ font: "ui-sm" })}>
                              Source
                            </Text>
                            <LinkOutIcon
                              styles={style({ size: 8, marginStart: 4 })}
                            />
                          </Link>
                        )}
                      </AssetCard>
                    </Card>
                  )}
                </CardView>
                {!query.isFetching && !query.data?.length && renderEmptyState()}
                {query.isLoading && renderLoadingState()}
              </DisclosurePanel>
            </Disclosure>
          );
        })}
      </Accordion>

      <DialogContainer onDismiss={() => setEditAction(null)}>
        {["edit", "add"].includes(editAction?.type) && (
          <Dialog>
            {({ close }) => (
              <>
                <Heading slot="title">
                  {editAction.type === "add" ? "Add link" : "Edit link"}
                </Heading>
                <Content>
                  {editAction.type === "add" && (
                    <p>Add new URL for image extraction</p>
                  )}
                  <Form>
                    <TextField
                      label="URL"
                      type="url"
                      defaultValue={editAction.url}
                      placeholder="https://"
                      onChange={(url) =>
                        setEditAction((oldValue) => ({ ...oldValue, url }))
                      }
                      isRequired
                    />
                    <TextField
                      label="Title"
                      defaultValue={editAction.title}
                      necessityIndicator="label"
                      onChange={(title) =>
                        setEditAction((oldValue) => ({ ...oldValue, title }))
                      }
                    />
                  </Form>
                </Content>
                <ButtonGroup>
                  <Button onPress={close} variant="secondary">
                    Cancel
                  </Button>
                  <Button
                    onPress={() => {
                      if (editAction.type === "add") {
                        handleAddUrl(editAction.url, editAction.title);
                      } else if (editAction.type === "edit") {
                        handleEditUrl(
                          editAction.url,
                          editAction.title,
                          editAction.index,
                        );
                      }

                      close();
                    }}
                    variant="accent"
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </>
            )}
          </Dialog>
        )}
        {editAction?.type === "delete" && (
          <AlertDialog
            variant="destructive"
            title="Delete link"
            primaryActionLabel="Delete"
            cancelLabel="Cancel"
            onPrimaryAction={() => {
              handleDeleteUrl(editAction.url);
              setEditAction(null);
            }}
            onCancel={() => setEditAction(null)}
          >
            <>
              <Text>Are you sure you want to delete this URL?</Text>
              <br />
              <Link
                href={editAction.url}
                target="_blank"
                variant="secondary"
                isStandalone
              >
                {editAction.url}
              </Link>
            </>
          </AlertDialog>
        )}
      </DialogContainer>
    </div>
  );
};

ScrapedImages.propTypes = {
  videoId: PropTypes.string,
  queries: PropTypes.array,
  onMenuAction: PropTypes.func,
};
